import { useEffect, useRef, useCallback } from "react";
import { playNotifChime } from "@/lib/notificationSound";
import { debugLog } from "@/lib/debugFlags";

/**
 * Live desktop-style notifications for inbound Hub messages.
 *
 * Responsibilities:
 *   1. Web Notification API — pop a native OS notification when the tab
 *      is not focused. Requests permission the first time.
 *   2. Favicon badge — draws a red dot on the tab icon when there are
 *      unread messages. Cleared when the tab regains focus.
 *   3. Optional beep — plays a short chime if the user has enabled the
 *      "sound_on_new_message" preference. Not required by product.
 *
 * Kept independent from the InboxPage so the same behaviour applies on
 * any page (admin, settings…) — the hook watches only the raw WS events.
 *
 * `getConversationTitle(evt)` is an optional callback the caller may pass
 * to produce a nicer notification title (client + sender). If absent, we
 * fall back to the sender_name embedded in the message.
 */
export function useMessageNotifications({
    getConversationTitle = null,
    onNotificationClick = null,
    soundEnabled = false,
} = {}) {
    const permissionRef = useRef(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
    const originalFaviconRef = useRef(null);
    const badgedFaviconRef = useRef(null);
    const badgeCountRef = useRef(0);

    // Ask permission (lazy — only when the user has been active >5s to
    // avoid the annoying instant popup on page load).
    useEffect(() => {
        if (permissionRef.current !== "default") return;
        const t = setTimeout(() => {
            if (typeof Notification !== "undefined" && Notification.permission === "default") {
                Notification.requestPermission().then(p => { permissionRef.current = p; });
            }
        }, 5000);
        return () => clearTimeout(t);
    }, []);

    // Prep favicon canvas (once) + emit a mount log so `?debug=notif` shows
    // us the hook actually ran (proves it survived the mobile refactor).
    useEffect(() => {
        const link = document.querySelector("link[rel='icon']") || document.querySelector("link[rel='shortcut icon']");
        if (!link) return;
        originalFaviconRef.current = link.href;
        debugLog("notif", "hook mounted — Toaster/Notification pipeline active", {
            device: window.innerWidth < 768 ? "mobile" : "desktop",
            perm: typeof Notification !== "undefined" ? Notification.permission : "n/a",
        });
    }, []);

    // Clear badge when tab becomes visible again
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                badgeCountRef.current = 0;
                restoreFavicon();
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    const restoreFavicon = () => {
        const link = document.querySelector("link[rel='icon']") || document.querySelector("link[rel='shortcut icon']");
        if (link && originalFaviconRef.current) {
            link.href = originalFaviconRef.current;
        }
        // Reset title
        if (document.title.startsWith("(")) {
            document.title = document.title.replace(/^\(\d+\)\s+/, "");
        }
    };

    const paintFaviconBadge = (count) => {
        if (!originalFaviconRef.current) return;
        try {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = 32; canvas.height = 32;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, 32, 32);
                // red dot in top-right
                ctx.fillStyle = "#ef4444";
                ctx.beginPath();
                ctx.arc(23, 9, 9, 0, Math.PI * 2);
                ctx.fill();
                // number
                ctx.fillStyle = "white";
                ctx.font = "bold 12px sans-serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(count > 9 ? "9+" : String(count), 23, 10);
                const dataUrl = canvas.toDataURL("image/png");
                const link = document.querySelector("link[rel='icon']") || document.querySelector("link[rel='shortcut icon']");
                if (link) link.href = dataUrl;
                badgedFaviconRef.current = dataUrl;
            };
            img.onerror = () => {
                // Fallback: at least prefix the title
            };
            img.src = originalFaviconRef.current;
        } catch (_e) { /* ignore */ }
        // Always prefix the title as a secondary signal
        const clean = document.title.replace(/^\(\d+\)\s+/, "");
        document.title = `(${count > 9 ? "9+" : count}) ${clean}`;
    };

    const notify = useCallback((evt) => {
        // Only inbound messages fire notifications; outbound is our own send.
        if (evt?.type !== "message.new") {
            debugLog("notif", "notify() skipped — wrong type:", evt?.type);
            return;
        }
        const msg = evt.message || {};
        if (msg.direction !== "in") {
            debugLog("notif", "notify() skipped — outbound message");
            return;
        }

        const senderName = msg.sender_name || "Alguien";
        const preview = (msg.body || "").slice(0, 120);
        debugLog("notif", "notify() firing —", {
            sender: senderName,
            visibility: document.visibilityState,
            permission: typeof Notification !== "undefined" ? Notification.permission : "n/a",
            soundEnabled,
            conv: evt.conversation_id,
        });

        // ---------- Web Notification API ----------
        if (document.visibilityState === "hidden"
            && typeof Notification !== "undefined"
            && Notification.permission === "granted") {
            const title = getConversationTitle
                ? getConversationTitle(evt) || `Nuevo mensaje de ${senderName}`
                : `Nuevo mensaje de ${senderName}`;
            try {
                const n = new Notification(title, {
                    body: preview,
                    tag: `hub-conv-${evt.conversation_id}`,   // collapses duplicates
                    icon: originalFaviconRef.current || undefined,
                    silent: !soundEnabled,
                });
                n.onclick = () => {
                    window.focus();
                    onNotificationClick?.(evt);
                    n.close();
                };
                debugLog("notif", "Web Notification fired:", title);
            } catch (e) { debugLog("notif", "Web Notification failed:", e); }
        } else {
            debugLog("notif", "Web Notification SKIPPED —",
                document.visibilityState !== "hidden" ? "tab is visible" :
                typeof Notification === "undefined" ? "no Notification API" :
                `permission=${Notification.permission}`);
        }

        // ---------- Favicon badge ----------
        if (document.visibilityState === "hidden") {
            badgeCountRef.current += 1;
            paintFaviconBadge(badgeCountRef.current);
            debugLog("notif", "Favicon badge painted count=", badgeCountRef.current);
        }

        // ---------- Optional beep ----------
        // Fires via the shared singleton AudioContext (see
        // /app/frontend/src/lib/notificationSound.js). The singleton is
        // unlocked on the first user gesture — anything before that will
        // log a warning and stay silent, which is why we ALSO surface an
        // "Habilitar sonido" banner in the inbox (see AudioReadinessBanner).
        if (soundEnabled) {
            // Fire-and-forget; the fn already swallows every failure mode
            // (suspended context, playback error, missing WebAudio API).
            debugLog("notif", "Playing chime");
            playNotifChime();
        }
    }, [getConversationTitle, onNotificationClick, soundEnabled]);

    return { notify };
}
